import { memo } from '@tanstack/react-table';

import {
  DETAILS_ROW_PARITY,
  DETAILS_ROW_IDENTIFIER,
  ROW_EXPANDED_ID_SUFFIX,
} from './RowDetails.js';
import type { RowModel, Row, Table } from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type { DataTableV2RowData } from '../../public.api.js';

export function getRowDetailsRowModel<TData extends DataTableV2RowData>(): (
  table: Table<TData>,
) => () => RowModel<TData> {
  return (table) => {
    return memo(
      () => [table.getState().rowDetails, table.getPreRowDetailsRowModel()],
      (rowDetails, rowModel) => {
        if (!rowModel.rows.length) {
          return rowModel;
        }

        const { rows, flatRows, rowsById } = rowModel;

        const detailsRowModel = expandRowDetails({
          rows,
          flatRows,
          rowsById,
        });

        return detailsRowModel;
      },
      getMemoOptions(table.options, 'debugRows', 'rowDetailsRowModel'),
    );
  };
}

function expandRowDetails<TData extends DataTableV2RowData>(
  rowModel: RowModel<TData>,
) {
  const detailsRows: Row<TData>[] = [];

  rowModel.rows.forEach((row, index) => {
    const parity = index % 2 === 0 ? 'even' : 'odd';
    row[DETAILS_ROW_PARITY] = parity;
    detailsRows.push(row);
    if (row.getIsDetailsOpen()) {
      const clonedRow: Row<TData> = {
        ...row,
        id: `${row.id}${ROW_EXPANDED_ID_SUFFIX}`,
        [DETAILS_ROW_IDENTIFIER]: true,
        // Make sure that the cloned row will not be selectable.
        getCanSelect: () => false,
        // Make sure that the cloned row will not show up in the subrows
        getCanExpand: () => false,
        // Make sure that the cloned row will be activateable
        getCanActivateRow: () => false,
      };
      detailsRows.push(clonedRow);
      // Add the cloned row the the byId mapping as well, so that it can be looked up
      rowModel.rowsById[clonedRow.id] = clonedRow;
    }
  });

  return {
    rows: detailsRows,
    flatRows: rowModel.flatRows,
    rowsById: rowModel.rowsById,
  };
}
